import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { exportReportTablePdf } from "../../../utils/reports/reportPdfExport";
import { ReportDateRangePicker } from "../../../components/DatePicker/ReportDateRangePicker";
import {
  PROFILE_FONT,
} from "../../../constants/layout";
import reportsService from "../../../redux/Reports/reportsService";
import {
  MF_POINT_TO_POINT_REPORT,
  MF_QUICK_REPORTS,
  MF_REPORT_TYPE_OPTIONS,
  NON_INDIVIDUAL_USER_TYPE,
  reportTypeNeedsDateRange,
} from "./mfReportConfig";
import {
  buildEffectiveColumns,
  buildSelectableColumns,
  getDefaultVisibleColumnKeys,
  REPORT_BUILDER_VISIBLE_COLUMN_COUNT,
} from "./mfReportColumnBuild";
import MfProfessionalReportTable from "./MfProfessionalReportTable";
import MfReportMainTable from "./MfReportMainTable";
import { filterReportRowsBySearch } from "./mfReportRowSearch";
import {
  buildUserMetaFromInvestors,
  mergeTransactionPages,
  parseMfReportResponse,
} from "./mfReportParsing";
import {
  getQuickReportRowCardSx,
  REPORT_BUILDER_CHIP_SELECTED_SX,
  REPORT_BUILDER_CHIP_UNSELECTED_SX,
  REPORT_BUILDER_GRID_SPACING,
  REPORT_BUILDER_SELECT_SX,
  REPORT_CHECKBOX_SX,
  REPORT_COLORS,
  REPORT_GENERATE_BUTTON_SX,
  REPORT_INPUT_SX,
  REPORT_INVESTOR_AUTOCOMPLETE_SX,
  REPORT_MENU_PAPER_SX,
  REPORT_QUICK_REPORTS_ROW_SX,
  REPORT_TYPO,
} from "./mfReportsTheme";
const HOLDINGS_PAGE_SIZE = 100;
const BUILDER_FIELD_COL = { xs: 12, lg: 4 } as const;

type InvestorListboxProps = React.HTMLAttributes<HTMLElement> & {
  allSelected: boolean;
  someSelected: boolean;
  onSelectAll: () => void;
};

const InvestorListbox = React.forwardRef<HTMLUListElement, InvestorListboxProps>(
  function InvestorListbox(
    { children, allSelected, someSelected, onSelectAll, ...other },
    ref,
  ) {
    return (
      <Box component="ul" ref={ref} {...other} sx={{ p: 0, m: 0 }}>
        <Box
          component="li"
          sx={{
            listStyle: "none",
            px: 1.5,
            py: 0.75,
            borderBottom: `1px solid ${REPORT_COLORS.border}`,
            position: "sticky",
            top: 0,
            bgcolor: REPORT_COLORS.white,
            zIndex: 1,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <FormControlLabel
            sx={{ m: 0, width: "100%" }}
            control={
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onSelectAll}
                sx={REPORT_CHECKBOX_SX}
              />
            }
            label={
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 600 }}>
                Select All
              </Typography>
            }
          />
        </Box>
        {children}
      </Box>
    );
  },
);

const QUICK_REPORT_ICONS: Record<string, React.ElementType> = {
  holding: DescriptionOutlinedIcon,
  realized: TrendingUpOutlinedIcon,
  "transaction-report": ReceiptLongOutlinedIcon,
  transaction: SwapHorizOutlinedIcon,
  p2p: TimelineOutlinedIcon,
};

const ASSET_CLASS_OPTIONS = [
  { value: "MUTUAL_FUND", label: "Mutual Funds" },
  { value: "AIF", label: "AIF" },
  { value: "PMS", label: "PMS" },
  { value: "SIF", label: "SIF" },
  { value: "STOCK", label: "Equity" },
  { value: "PRIVATE_STOCK", label: "Unlisted Equity" },
  { value: "INTERNATIONAL_EQUITY", label: "International Equity" },
  { value: "REIT", label: "REIT/INVIT" },
  { value: "INSURANCE", label: "Insurance" },
  { value: "OTHER", label: "Other Asset" },
  { value: "BOND", label: "Bonds" },
  { value: "FD", label: "Fixed Deposit" },
];

type InvestorOption = {
  id: number;
  label: string;
  firstName: string;
  aumUserId?: string;
  email?: string;
  mobile?: string;
  userType?: number;
};

type Props = {
  users: {
    id: number;
    userId?: number;
    firstName: string;
    aumUserId?: string;
    email?: string;
    mobile?: string;
    userType?: number;
  }[];
  userType: string | null;
  assetClass: string;
  onAssetClassChange: (value: string) => void;
  selectedInvestorIds: number[];
  onSelectedInvestorIdsChange: (ids: number[]) => void;
};

const MfInvestorReportPanel: React.FC<Props> = ({
  users,
  userType,
  assetClass,
  onAssetClassChange,
  selectedInvestorIds,
  onSelectedInvestorIdsChange,
}) => {
  const [reportType, setReportType] = useState("");
  const [startDate, setStartDate] = useState<number | undefined>();
  const [endDate, setEndDate] = useState<number | undefined>();
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);
  const [builderMoreColumnsAnchor, setBuilderMoreColumnsAnchor] =
    useState<null | HTMLElement>(null);
  const [rowData, setRowData] = useState<any[]>([]);
  const [displayRows, setDisplayRows] = useState<any[]>([]);
  const [totalHoldingInfo, setTotalHoldingInfo] = useState<Record<string, unknown> | null>(null);
  const [taxTotalGainLoss, setTaxTotalGainLoss] = useState<number | null>(null);
  const [tableSearch, setTableSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const investorOptions = useMemo<InvestorOption[]>(
    () =>
      users
        .map((u) => {
          const id = u.userId ?? u.id;
          return {
            id,
            label: `${u.firstName} (${u.aumUserId ?? id})`,
            firstName: u.firstName,
            aumUserId: u.aumUserId,
            email: u.email,
            mobile: u.mobile,
            userType: u.userType,
          };
        })
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        ),
    [users],
  );

  const selectedInvestors = useMemo(
    () => investorOptions.filter((o) => selectedInvestorIds.includes(o.id)),
    [investorOptions, selectedInvestorIds],
  );

  const setSelectedInvestors = (investors: InvestorOption[]) => {
    onSelectedInvestorIdsChange(investors.map((i) => i.id));
  };

  /** Selected investors pinned to top; rest alphabetical */
  const sortedInvestorOptions = useMemo(() => {
    const selectedIds = new Set(selectedInvestors.map((i) => i.id));
    const selected = investorOptions.filter((o) => selectedIds.has(o.id));
    const unselected = investorOptions.filter((o) => !selectedIds.has(o.id));
    return [...selected, ...unselected];
  }, [investorOptions, selectedInvestors]);

  const allInvestorsSelected =
    investorOptions.length > 0 &&
    selectedInvestors.length === investorOptions.length;
  const someInvestorsSelected =
    selectedInvestors.length > 0 && !allInvestorsSelected;

  const handleSelectAllInvestors = () => {
    setSelectedInvestors(allInvestorsSelected ? [] : [...investorOptions]);
  };

  /** Always show first investor + +N — even while dropdown is open (MUI limitTags only applies when blurred). */
  const renderInvestorTags = useCallback(
    (
      value: InvestorOption[],
      getTagProps: (args: { index: number }) => Record<string, unknown>,
    ) => {
      if (!value.length) return [];
      const first = value[0];
      const restCount = value.length - 1;
      const { key, ...tagProps } = getTagProps({ index: 0 });
      const nodes: React.ReactNode[] = [
        <Chip
          key={String(key)}
          label={first.label}
          size="small"
          {...tagProps}
          sx={{
            maxWidth: restCount > 0 ? "calc(100% - 48px)" : "100%",
            height: 24,
            fontFamily: PROFILE_FONT,
            fontSize: 12,
            bgcolor: "rgba(30, 58, 95, 0.08)",
            color: REPORT_COLORS.button,
            border: `1px solid rgba(30, 58, 95, 0.2)`,
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              px: 1,
            },
          }}
        />,
      ];
      if (restCount > 0) {
        nodes.push(
          <Typography
            key="investor-more-count"
            component="span"
            sx={{
              flexShrink: 0,
              fontFamily: PROFILE_FONT,
              fontSize: 12,
              fontWeight: 600,
              color: REPORT_COLORS.button,
              lineHeight: "24px",
              userSelect: "none",
            }}
          >
            +{restCount}
          </Typography>,
        );
      }
      return nodes;
    },
    [],
  );

  const InvestorListboxWithSelectAll = useMemo(
    () =>
      React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>(
        function InvestorListboxWithSelectAll(props, ref) {
          return (
            <InvestorListbox
              ref={ref}
              {...props}
              allSelected={allInvestorsSelected}
              someSelected={someInvestorsSelected}
              onSelectAll={handleSelectAllInvestors}
            />
          );
        },
      ),
    [allInvestorsSelected, someInvestorsSelected, investorOptions.length],
  );

  const selectedUserIds = useMemo(
    () => selectedInvestors.map((u) => u.id),
    [selectedInvestors],
  );

  const hasNonIndividualSelected = useMemo(
    () =>
      selectedInvestors.some((u) => u.userType === NON_INDIVIDUAL_USER_TYPE),
    [selectedInvestors],
  );

  const availableReportTypes = useMemo(
    () =>
      MF_REPORT_TYPE_OPTIONS.filter(
        (opt) => opt.value !== "All" || hasNonIndividualSelected,
      ),
    [hasNonIndividualSelected],
  );

  const selectableColumns = useMemo(() => {
    if (!reportType) return [];
    return buildSelectableColumns(reportType);
  }, [reportType]);

  const activeColumns = useMemo(
    () =>
      reportType ? buildEffectiveColumns(reportType, visibleColumnKeys) : [],
    [reportType, visibleColumnKeys],
  );

  const builderPrimaryColumns = selectableColumns.slice(
    0,
    REPORT_BUILDER_VISIBLE_COLUMN_COUNT,
  );
  const builderMoreColumns = selectableColumns.slice(
    REPORT_BUILDER_VISIBLE_COLUMN_COUNT,
  );
  const shouldUseMoreMenu = builderMoreColumns.length > 3;
  const inlineExtraColumns = shouldUseMoreMenu ? [] : builderMoreColumns;
  const menuMoreColumns = shouldUseMoreMenu ? builderMoreColumns : [];

  const toggleVisibleColumn = (colKey: string, locked?: boolean) => {
    if (locked) return;
    setVisibleColumnKeys((prev) => {
      const current =
        prev.length > 0 ? prev : selectableColumns.map((c) => c.key);
      return current.includes(colKey)
        ? current.filter((k) => k !== colKey)
        : [...current, colKey];
    });
  };

  const renderColumnChip = (col: { key: string; value: string; locked?: boolean }) => {
    const isSelected =
      !visibleColumnKeys.length || visibleColumnKeys.includes(col.key);
    return (
      <Chip
        key={col.key}
        label={col.value}
        variant={isSelected ? "filled" : "outlined"}
        onClick={() => toggleVisibleColumn(col.key, col.locked)}
        sx={
          isSelected
            ? REPORT_BUILDER_CHIP_SELECTED_SX
            : REPORT_BUILDER_CHIP_UNSELECTED_SX
        }
      />
    );
  };

  const filteredRows = useMemo(
    () => filterReportRowsBySearch(rowData, tableSearch),
    [rowData, tableSearch],
  );

  useEffect(() => {
    setDisplayRows(filteredRows);
  }, [filteredRows]);

  const handleReportTypeChange = (value: string) => {
    setReportType(value);
    setVisibleColumnKeys([]);
    setRowData([]);
    setDisplayRows([]);
    setTotalHoldingInfo(null);
    setTaxTotalGainLoss(null);
    setTableSearch("");
    setPageIndex(1);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const buildRequestBody = (page = 1) => {
    const body: Record<string, unknown> = {
      holdingType: "MUTUAL_FUND",
      reportType,
      userIds: selectedUserIds,
      userType,
      pageIndex: page,
      pageSize: HOLDINGS_PAGE_SIZE,
      forRm: userType === "2" ? 1 : userType === "6" ? 0 : -1,
      rmId: localStorage?.getItem("ID"),
    };

    if (reportTypeNeedsDateRange(reportType)) {
      if (reportType === MF_POINT_TO_POINT_REPORT) {
        if (startDate && endDate) {
          body.startDate = dayjs(startDate).format("YYYY-MM-DD");
          body.endDate = dayjs(endDate).format("YYYY-MM-DD");
        }
      } else if (startDate && endDate) {
        body.startDate = startDate;
        body.endDate = endDate;
      }
    }
    return body;
  };

  const fetchReport = useCallback(
    async (page = 1, append = false) => {
      if (!selectedUserIds.length) {
        toast.error("Select at least one investor");
        return;
      }
      if (!reportType) {
        toast.error("Select a report type");
        return;
      }
      if (reportTypeNeedsDateRange(reportType)) {
        if (!startDate || !endDate) {
          toast.error("Select a complete date range");
          return;
        }
      }

      append ? setIsFetchingMore(true) : setLoading(true);
      try {
        const response = await reportsService.investorReports(buildRequestBody(page));
        const holdingsData = response?.data?.data ?? response?.data;
        const userMeta = buildUserMetaFromInvestors(investorOptions, selectedUserIds);
        const parsed = parseMfReportResponse(
          reportType,
          holdingsData,
          userMeta,
          selectedUserIds,
        );

        setTotalPages(parsed.totalPages ?? 1);
        setTotalRecords(parsed.totalRecords ?? parsed.rows.length);
        setPageIndex(parsed.pageIndex ?? page);
        setTotalHoldingInfo(parsed.totalHoldingInfo ?? null);
        setTaxTotalGainLoss(parsed.taxTotalGainLoss ?? null);
        setRowData((prev) =>
          append ? mergeTransactionPages(prev, parsed.rows) : parsed.rows,
        );
        setShowReport(true);
        if (!append) {
          setVisibleColumnKeys(getDefaultVisibleColumnKeys(reportType));
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch MF report");
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    },
    [
      endDate,
      investorOptions,
      reportType,
      selectedUserIds,
      startDate,
      userType,
    ],
  );

  const isInfiniteScroll =
    reportType === "Transaction report" && totalPages > pageIndex;

  const handleScroll = () => {
    if (!isInfiniteScroll || isFetchingMore || loading) return;
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 120) {
      void fetchReport(pageIndex + 1, true);
    }
  };

  const exportExcel = () => {
    if (!displayRows.length) {
      toast.error("No data to export");
      return;
    }
    const sheet = displayRows.map((row) => {
      const out: Record<string, unknown> = {};
      for (const col of activeColumns) {
        out[col.value] = row[col.key] ?? "-";
      }
      return out;
    });
    const ws = XLSX.utils.json_to_sheet(sheet);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MF Report");
    XLSX.writeFile(wb, `MF_${reportType}_${Date.now()}.xlsx`);
  };

  const exportPdf = () => {
    if (!displayRows.length) {
      toast.error("No data to export");
      return;
    }
    try {
      exportReportTablePdf({
        fileName: `MF_${reportType}_${Date.now()}.pdf`,
        reportTitle: `Mutual Fund – ${reportType}`,
        columns: activeColumns,
        rows: displayRows,
        formatCell: (_key, value) => {
          if (value === null || value === undefined || value === "") return "-";
          return String(value);
        },
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to export PDF");
    }
  };

  const showDateRange = Boolean(reportType && reportTypeNeedsDateRange(reportType));

  return (
    <>
      <Typography sx={REPORT_TYPO.sectionHeading}>Custom Report Builder</Typography>
      <Typography sx={{ ...REPORT_TYPO.pageSubtitle, mb: { xs: 1.5, md: 2, lg: 2.5 } }}>
        Configure asset class, date range, and columns for export-ready reports.
      </Typography>

      <Grid container spacing={REPORT_BUILDER_GRID_SPACING} mt={{ xs: 0.5, md: 1 }}>
        {/* Investor — multi-select dropdown */}
        <Grid item {...BUILDER_FIELD_COL}>
          <Typography sx={REPORT_TYPO.fieldLabel}>Investor Name</Typography>
          <Autocomplete
            multiple
            size="small"
            options={sortedInvestorOptions}
            value={selectedInvestors}
            onChange={(_, value) => setSelectedInvestors(value)}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disableCloseOnSelect
            renderTags={renderInvestorTags}
            ListboxComponent={InvestorListboxWithSelectAll}
            slotProps={{
              paper: { sx: REPORT_MENU_PAPER_SX },
            }}
            sx={REPORT_INVESTOR_AUTOCOMPLETE_SX}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox
                  size="small"
                  checked={selected}
                  sx={{ ...REPORT_CHECKBOX_SX, mr: 1 }}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedInvestors.length ? "" : "Select investor(s)"
                }
              />
            )}
          />
        </Grid>

        {/* Segment — MF pre-selected */}
        <Grid item {...BUILDER_FIELD_COL}>
          <Typography sx={REPORT_TYPO.fieldLabel}>Segment</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={assetClass}
              onChange={(e) => onAssetClassChange(e.target.value)}
              sx={REPORT_BUILDER_SELECT_SX}
              MenuProps={{ PaperProps: { sx: REPORT_MENU_PAPER_SX } }}
            >
              {ASSET_CLASS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Report Type */}
        <Grid item {...BUILDER_FIELD_COL}>
          <Typography sx={REPORT_TYPO.fieldLabel}>Report Type</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={reportType}
              displayEmpty
              onChange={(e) => handleReportTypeChange(e.target.value)}
              sx={REPORT_BUILDER_SELECT_SX}
              MenuProps={{ PaperProps: { sx: REPORT_MENU_PAPER_SX } }}
            >
              <MenuItem value="" disabled>
                Select Report Type
              </MenuItem>
              {availableReportTypes.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Quick Reports — always visible for MF */}
        <Grid item xs={12}>
          <Typography sx={{ ...REPORT_TYPO.sectionTitle, mb: { xs: 0.75, md: 1 } }}>
            Quick Reports
          </Typography>
          <Box sx={REPORT_QUICK_REPORTS_ROW_SX}>
            {MF_QUICK_REPORTS.filter(
              (q) => q.reportType !== "All" || hasNonIndividualSelected,
            ).map((item) => {
              const Icon = QUICK_REPORT_ICONS[item.id] || DescriptionOutlinedIcon;
              const active = reportType === item.reportType;
              return (
                <Box
                  key={item.id}
                  onClick={() => handleReportTypeChange(item.reportType)}
                  sx={getQuickReportRowCardSx(active)}
                >
                  <Icon
                    sx={{
                      fontSize: { xs: 22, sm: 26 },
                      color: active ? REPORT_COLORS.button : REPORT_COLORS.mutedText,
                      mb: { xs: 0.5, sm: 1 },
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: PROFILE_FONT,
                      fontSize: { xs: 12, sm: 13 },
                      fontWeight: active ? 600 : 500,
                      color: active ? REPORT_COLORS.button : REPORT_COLORS.primaryText,
                      textAlign: "center",
                      lineHeight: 1.3,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>

        {/* Date range — only after report type selected */}
        {showDateRange && (
          <Grid item {...BUILDER_FIELD_COL}>
            <Typography sx={REPORT_TYPO.fieldLabel}>Date Range</Typography>
            <ReportDateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={({ startDate: s, endDate: e }: { startDate?: number; endDate?: number }) => {
                setStartDate(s);
                setEndDate(e);
              }}
            />
          </Grid>
        )}

        {/* Select Columns — only after report type selected */}
        {reportType && selectableColumns.length > 0 && (
          <Grid item xs={12}>
            <Typography sx={{ ...REPORT_TYPO.sectionTitle, mb: { xs: 0.75, md: 1 } }}>
              Select Columns
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 0.5, sm: 0.75, md: 1 },
                alignItems: "center",
              }}
            >
              {builderPrimaryColumns.map((col) => renderColumnChip(col))}
              {inlineExtraColumns.map((col) => renderColumnChip(col))}
              {menuMoreColumns.length > 0 && (
                <>
                  <Chip
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        More
                        <KeyboardArrowDown sx={{ fontSize: { xs: 16, md: 18 } }} />
                      </Box>
                    }
                    onClick={(e) => setBuilderMoreColumnsAnchor(e.currentTarget)}
                    sx={REPORT_BUILDER_CHIP_UNSELECTED_SX}
                  />
                  <Menu
                    anchorEl={builderMoreColumnsAnchor}
                    open={Boolean(builderMoreColumnsAnchor)}
                    onClose={() => setBuilderMoreColumnsAnchor(null)}
                    PaperProps={{
                      sx: { borderRadius: 2, minWidth: 220, mt: 0.5 },
                    }}
                  >
                    {menuMoreColumns.map((col) => {
                      const isSelected =
                        !visibleColumnKeys.length ||
                        visibleColumnKeys.includes(col.key);
                      return (
                        <MenuItem
                          key={col.key}
                          onClick={() => toggleVisibleColumn(col.key, col.locked)}
                          sx={{ fontSize: 14 }}
                        >
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            disabled={col.locked}
                            sx={{ p: 0.5, mr: 1 }}
                          />
                          {col.value}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              )}
            </Box>
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button
          variant="contained"
          disableElevation
          sx={REPORT_GENERATE_BUTTON_SX}
          disabled={loading || !selectedUserIds.length || !reportType}
          onClick={() => {
            setPageIndex(1);
            void fetchReport(1, false);
          }}
        >
          {loading ? "Generating..." : "Generate Report"}
        </Button>
      </Box>

      {showReport && (
        <MfProfessionalReportTable
          loading={loading}
          rowData={displayRows}
          controlBarObj={activeColumns}
          segment="Mutual Funds"
          reportType={reportType}
          totalHoldingInfo={totalHoldingInfo}
          taxReportTotalGainLoss={taxTotalGainLoss}
          searchQuery={tableSearch}
          onSearchQueryChange={setTableSearch}
          totalUnfilteredCount={rowData.length}
          tableScrollRef={scrollRef}
          showPagination={
            reportType === "Transaction report" && totalPages > 1
          }
          pageIndex={pageIndex}
          totalPages={totalPages}
          onPageChange={(p) => void fetchReport(p, false)}
          onExportCsv={exportExcel}
          onExportPdf={exportPdf}
          onScroll={handleScroll}
        >
          <MfReportMainTable
            headData={activeColumns}
            bodyData={displayRows}
            unSortdata={setDisplayRows}
            segment="Mutual Funds"
            reportType={reportType}
          />
        </MfProfessionalReportTable>
      )}

      {isFetchingMore && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}
    </>
  );
};

export default MfInvestorReportPanel;
